
import axios from "@/axios-instance";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import Permission from "@/core/config/Permission";
import { debounce } from "debounce";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { defineComponent, ref } from "vue";
import { exportStyledFromHeaderMap } from "@/core/helpers/exportXlsx";
import { MenuComponent } from "@/assets/ts/components"; //kebab dùng menu Metronic (data-kt-menu)
import * as XLSX from "xlsx-js-style"; //bản gửi P.ĐT dựng tay theo mẫu (có phần đầu thư + khối ký tên)

interface ICustomer {
    id: string;
    studentId: string;
    scholarshipCode: string;
    profileCode: string;
    lastName: string;
    firstName: string;
    fullName: string;
    gender: string;
    dateOfBirth: string;
    schoolName: string;
    schoolProvince: string;
    majorName: string;
    scholarshipType: string;
    releasedIn: string;
    rate: string;
    scholarshipValue: number;
    description: string;
}

interface IPagination {
    currentPage: number;
    total: number;
    limit: number;
}

export default defineComponent({
    name: "customers-listing",
    components: {
        Datatable,
    },
    data() {
        return {
            loading: false,
            exportingPdt: false, //chống bấm 2 lần nút xuất P.ĐT (có gọi API riêng)
            filter: {
                method: 0,   //umt_test | english_certificate | english_thpt | none | __empty (chưa khai)
                status: 0,   //completed (Chờ xác thực) | confirmed (Đã hoàn tất)
                payment: 0,  //paid | received | pdt (đóng tiền HOẶC TNHS) | none
            } as any,
        }
    },
    watch: {
        //search đi CHUNG đường với dropdown (applyFilter) để 2 thứ cộng dồn, không cái nào xoá cái nào
        search: debounce(async function (this) {
            this.applyFilter()
        }, 300)
    },
    methods: {
        // Lọc client-side: luôn dựng lại từ initCustomers rồi áp lần lượt hình thức -> trạng thái -> tình trạng -> từ khoá
        applyFilter() {
            const f = this.filter
            let rows = (this.initCustomers as any[]).slice()
            if (f.method) {
                rows = f.method === '__empty'
                    ? rows.filter((i: any) => !i.registrationType)
                    : rows.filter((i: any) => i.registrationType === f.method)
            }
            if (f.status) {
                rows = rows.filter((i: any) => i.status === f.status)
            }
            if (f.payment) {
                rows = rows.filter((i: any) => {
                    if (f.payment === 'paid') return i.paid
                    if (f.payment === 'received') return i.received
                    if (f.payment === 'pdt') return i.paid || i.received
                    return !i.paid && !i.received //none
                })
            }
            const kw = String(this.search || '').trim()
            if (kw) {
                rows = rows.filter((i: any) => this.searchingFunc(i, kw))
            }
            this.pagination.total = rows.length
            this.pagination.currentPage = 1
            this.tableData = rows
        },
        resetFilter() {
            this.filter = { method: 0, status: 0, payment: 0 }
            this.search = ''
            this.applyFilter()
        },
        statusLabel(status: any) {
            return status == 'confirmed' ? 'Đã hoàn tất' : status == 'completed' ? 'Chờ xác thực' : (status || '')
        },
        // Ghi chú phải khớp ĐÚNG 2 hàm xuất bên dưới — sửa exportFile/exportForPdt thì cập nhật lại đây.
        showExportInfo() {
            Swal.fire({
                title: 'Hai file xuất khác nhau thế nào?',
                width: 700,
                html: `<div class="text-start fs-7 eng-info-body">
                    <div class="mb-1">
                        <b class="text-success">Xuất DS đang xem (nội bộ)</b> — chụp lại đúng bảng đang hiển thị để rà soát nội bộ.
                    </div>
                    <ul class="ps-4 mb-3 text-gray-700">
                        <li>Đi <b>theo bộ lọc</b> Hình thức / Trạng thái / Tình trạng hồ sơ và ô tìm kiếm.</li>
                        <li>Có cột Hình thức xếp lớp, Trạng thái hồ sơ, Tình trạng hồ sơ như trên màn hình.</li>
                    </ul>
                    <div class="mb-1">
                        <b class="text-primary">Xuất dữ liệu xếp lớp (P.ĐT)</b> — file gửi <b>Phòng Đào tạo</b>, dựng đúng mẫu
                        <i>“Mẫu_du lieu xep lop tieng Anh.xlsx”</i> (đầu thư, khối Tổng và 2 ô ký tên).
                    </div>
                    <ul class="ps-4 mb-3 text-gray-700">
                        <li><b>Không</b> theo bộ lọc trên màn hình — luôn lấy lại dữ liệu từ hệ thống.</li>
                        <li>Gồm <b>mọi hình thức</b> xếp lớp (UMT test, chứng chỉ, điểm TN THPT…), sắp theo tên A→Z.</li>
                        <li>Chỉ lấy SV <b>đã đóng tiền hoặc đã tiếp nhận hồ sơ</b>.</li>
                    </ul>
                    <div class="eng-info-warn rounded p-3 text-gray-700">
                        <b class="text-danger">File P.ĐT thường ít dòng hơn danh sách</b> — vì lọc thêm điều kiện đã đóng tiền / đã TNHS.
                        Muốn xem trước đúng tập hợp đó, chọn bộ lọc <b>“Đã đóng tiền hoặc TNHS (mẫu P.ĐT)”</b> ở ô Tình trạng hồ sơ.
                    </div>
                </div>`,
                buttonsStyling: false,
                confirmButtonText: 'Đã hiểu',
                //bỏ trần 200px của .swal2-html-container (theme Metronic) để nội dung không sinh thanh cuộn
                customClass: { popup: 'eng-export-info-popup', confirmButton: 'btn btn-primary' },
            })
        },
        // Nhãn NGẮN cho bảng (bản dài englishMethodLabel dành cho file gửi P.ĐT)
        englishMethodShort(t: any) {
            if (t == 'umt_test') return 'Thi xếp lớp (UMT test)'
            if (t == 'english_certificate') return 'Chứng chỉ Tiếng Anh'
            if (t == 'english_thpt') return 'Điểm TN THPT'
            if (t == 'none') return 'Không kiểm tra xếp lớp'
            return 'Chưa khai'
        },
        removeAccents(str: string, text?: boolean) {
            var AccentsMap = ['aàảãáạăằẳẵắặâầẩẫấậ', 'AÀẢÃÁẠĂẰẲẴẮẶÂẦẨẪẤẬ', 'dđ', 'DĐ', 'eèẻẽéẹêềểễếệ', 'EÈẺẼÉẸÊỀỂỄẾỆ', 'iìỉĩíị', 'IÌỈĨÍỊ', 'oòỏõóọôồổỗốộơờởỡớợ', 'OÒỎÕÓỌÔỒỔỖỐỘƠỜỞỠỚỢ', 'uùủũúụưừửữứự', 'UÙỦŨÚỤƯỪỬỮỨỰ', 'yỳỷỹýỵ', 'YỲỶỸÝỴ',]
            for (var i = 0; i < AccentsMap.length; i++) {
                var re = new RegExp('[' + AccentsMap[i].substr(1) + ']', 'g')
                var char = AccentsMap[i][0]
                str = str.replace(re, char).toLowerCase()
            }
            if (text) {
                return str
            }
            return str/* .replace(/ /g, '-').replace(/[^\w-]+/g, '') */
        },
        sortWithAny(values: any[], updates: { sort: string }) {
            if (!Array.isArray(values) || (Array.isArray(values) && values.length == 0)) return []
            let tempValues: Array<any> = values
            let sortColumn: string = String(updates.sort)
            tempValues = tempValues.sort((a: any, b: any) => {
                if (typeof a[sortColumn] === 'string') {
                    return ('' + b[sortColumn]).localeCompare(a[sortColumn], undefined, {
                        numeric: true,
                        sensitivity: 'base',
                    })
                }
                return Number(b[sortColumn]) - Number(a[sortColumn])
            })
            return tempValues
        },
        async exportFile() {
            Swal.fire({
                html: "<div class='custom-loading'><span>Đang xử lý. Vui lòng chờ trong giây lát!!!</span> <span class='loader'></span></div>",
                buttonsStyling: false,
                timerProgressBar: true,
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false,
                showConfirmButton: false,
            })
            // Tên cột nói ĐÚNG nội dung ô: testdate KHÔNG phải ngày đăng ký (nó chứa "Xếp theo lịch của
            // Trường"), createdAt là ngày tạo PHIẾU NHẬP HỌC chứ không phải ngày đăng ký thi.
            // Thêm "Tình trạng hồ sơ" để rà được vì sao bản gửi P.ĐT ít dòng hơn (lọc đóng tiền/TNHS).
            const header = {
                studentCode: "MSSV",
                fullName: "Họ Tên",
                major: "Ngành nhập học",
                programtype: "Chương trình học",
                englishMethod: "Hình thức xếp lớp",
                testdate: "Lịch thi xếp lớp",
                statusLabel: "Trạng thái hồ sơ",
                paymentStatus: "Tình trạng hồ sơ",
                phase: "Đợt",
                createdAt: "Ngày tạo hồ sơ nhập học",
            }
            // Danh sách không còn chỉ UMT test -> tiêu đề/tên file đi theo bộ lọc Hình thức đang chọn,
            // không thì file ghi "(UMT TEST)" mà bên trong có cả IELTS/điểm THPT.
            const onlyUmt = this.filter.method === 'umt_test'
            const title = onlyUmt
                ? 'DS ĐĂNG KÝ KIỂM TRA XẾP LỚP TIẾNG ANH DO UMT TỔ CHỨC (UMT TEST)'
                : 'DS ĐĂNG KÝ XẾP LỚP TIẾNG ANH'
            // FE dựng file styled (bỏ /utilities/exportexcel — json2excel không style được)
            try {
                exportStyledFromHeaderMap({
                    title,
                    fileBaseName: onlyUmt ? 'ds-dang-ky-kiem-tra-tieng-anh-umt-test' : 'ds-dang-ky-xep-lop-tieng-anh',
                    sheetName: onlyUmt ? 'Đăng ký thi UMT' : 'Xếp lớp Tiếng Anh',
                    summary: `Tổng: ${this.tableData.length} sinh viên`,
                    header,
                    items: this.tableData,
                    widths: { fullName: 24, major: 28, englishMethod: 24, paymentStatus: 22, createdAt: 22 },
                })
                Swal.close()
            } catch (error) {
                console.log('err', error)
                Swal.close()
            }
        },
        // "Đã đóng tiền hoặc TNHS" — điều kiện lọc của bản gửi P.ĐT, dùng chung với cột Tình trạng hồ sơ.
        // paidBy: cột applicants ghi hình thức đã thanh toán ('none' = chưa); isApplicationFormPrinted = đã TNHS.
        isPaid(item: any) {
            return !!(item?.paidBy && item.paidBy !== 'none')
        },
        isReceived(item: any) {
            return !!item?.isApplicationFormPrinted
        },
        paymentStatusLabel(item: any) {
            const parts: string[] = []
            if (this.isPaid(item)) parts.push('Đã đóng tiền')
            if (this.isReceived(item)) parts.push('Đã TNHS')
            return parts.join(' + ') || 'Chưa đóng tiền / chưa TNHS'
        },
        programtypeLabel(t: any) {
            return t == 'TIENG_VIET' ? 'Tiếng Việt' : t == 'SONG_NGU' ? 'Song ngữ' : ''
        },
        // dd/mm/yyyy; giá trị không phải ngày (chuỗi tự do SV nhập) thì giữ nguyên
        fmtDate(v: any) {
            if (!v) return ''
            const d = new Date(v)
            if (isNaN(d.getTime())) return String(v)
            const p = (n: number) => String(n).padStart(2, '0')
            return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`
        },
        // Hình thức xếp lớp Tiếng Anh — nhãn giống cổng nhập học để SV/P.ĐT đọc cùng một ngôn ngữ
        englishMethodLabel(reg: any) {
            const t = reg?.registrationType
            if (t == 'umt_test') return 'Thi xếp lớp theo lịch của trường'
            if (t == 'english_thpt') return 'Điểm tiếng Anh TN THPT'
            if (t == 'english_certificate') return reg?.englishCertificate?.certificateName || 'Chứng chỉ Tiếng Anh'
            if (t == 'none') return 'Không kiểm tra xếp lớp (học từ Tiếng Anh 1)'
            return ''
        },
        /**
         * Xuất file gửi PHÒNG ĐÀO TẠO theo mẫu "Mẫu_du lieu xep lop tieng Anh.xlsx" (bố cục cột/dòng
         * giữ ĐÚNG mẫu: 2 dòng tên trường/phòng, title + ngày xuất merge A:G, header dòng 7, cuối bảng
         * là dòng Tổng + 2 ô ký tên).
         * KHÁC bản nội bộ ở 2 điểm: (1) lấy MỌI hình thức xếp lớp Tiếng Anh (mẫu có cả IELTS/điểm THPT),
         * nên phải gọi API riêng — danh sách trên trang chỉ lọc umt_test; (2) chỉ thí sinh ĐÃ ĐÓNG TIỀN
         * hoặc ĐÃ TNHS.
         */
        async exportForPdt() {
            if (this.exportingPdt) return
            this.exportingPdt = true
            Swal.fire({
                html: "<div class='custom-loading'><span>Đang xử lý. Vui lòng chờ trong giây lát!!!</span> <span class='loader'></span></div>",
                buttonsStyling: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false,
                showConfirmButton: false,
            })
            try {
                const { data } = await axios.get('applicant?status=completed,confirmed')
                const rows = (data || [])
                    .filter((item: any) => item?.englishClassPlacementTestRegistration?.registrationType)
                    .filter((item: any) => this.isPaid(item) || this.isReceived(item))
                    .map((item: any) => {
                        const reg = item.englishClassPlacementTestRegistration || {}
                        const cert = reg.englishCertificate || {}
                        return {
                            studentCode: item.studentCode || '',
                            fullName: item.admissionRegistration?.fullName || '',
                            major: item.major || '',
                            programtype: this.programtypeLabel(item.programtype),
                            method: this.englishMethodLabel(reg),
                            score: cert.score || '',
                            expiredDate: reg.registrationType == 'english_certificate' ? this.fmtDate(cert.expiredDate) : '',
                            idNumber: item.idNumber || '',
                            phone: item.contactMobile || '',
                            email: item.contactEmail || '',
                            phase: item.phase || '',
                            createdAt: this.fmtDate(item.createdAt),
                        }
                    })
                    .sort((a: any, b: any) => String(a.fullName).localeCompare(String(b.fullName), 'vi'))
                if (!rows.length) {
                    Swal.fire({
                        text: 'Không có thí sinh nào đã đóng tiền hoặc đã TNHS để xuất.',
                        icon: 'info', buttonsStyling: false, confirmButtonText: 'Đóng',
                        customClass: { confirmButton: 'btn btn-primary' },
                    })
                    return
                }
                this.buildPdtWorkbook(rows)
                Swal.close()
            } catch (error) {
                console.log('err', error)
                Swal.fire({
                    text: 'Xuất file thất bại, vui lòng thử lại.',
                    icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng',
                    customClass: { confirmButton: 'btn btn-primary' },
                })
            }
            this.exportingPdt = false
        },
        // Dựng đúng bố cục mẫu của P.ĐT (không dùng exportStyledFromHeaderMap vì mẫu có phần đầu thư
        // và khối ký tên riêng). Cột Điểm/CCCD/SĐT ép định dạng TEXT: Excel không được cắt "5.0" thành 5
        // hay ăn mất số 0 đầu của CCCD/số điện thoại.
        buildPdtWorkbook(rows: any[]) {
            const TITLE = 'DS ĐĂNG KÝ XẾP LỚP TIẾNG ANH (UMT TEST)'
            const HEADERS = ['STT', 'MSSV', 'Họ Tên', 'Ngành nhập học', 'Chương trình học',
                'Hình thức Xếp lớp Tiếng Anh', 'Điểm', 'Thời hạn chứng chỉ (dd/mm/yyyy)',
                'CCCD', 'Điện thoại', 'Email', 'Đợt', 'Ngày tạo']
            const now = new Date()
            const p = (n: number) => String(n).padStart(2, '0')
            const exportedAt = `${p(now.getDate())}/${p(now.getMonth() + 1)}/${now.getFullYear()} ${p(now.getHours())}:${p(now.getMinutes())}`
            const signCell = 'Ngày         tháng          năm'

            const aoa: any[][] = [
                ['', 'TRƯỜNG ĐẠI HỌC QUẢN LÝ VÀ CÔNG NGHỆ THÀNH PHỐ HỒ CHÍ MINH'],
                ['', '', 'PHÒNG TUYỂN SINH'],
                [],
                [TITLE],
                [`Ngày xuất: ${exportedAt}`],
                [],
                HEADERS,
                ...rows.map((r, i) => [i + 1, r.studentCode, r.fullName, r.major, r.programtype,
                    r.method, r.score, r.expiredDate, r.idNumber, r.phone, r.email, r.phase, r.createdAt]),
                [],
                // Khối ký: mẫu gốc để chữ ở cột D và G với mọi cột rộng bằng nhau; file này cột rộng
                // rất lệch nên phải MERGE + canh giữa từng khối, không thì "Ngày tháng năm" tràn sang
                // cột bên cạnh và lệch hẳn khỏi chữ "Người lập" bên dưới.
                ['', 'Tổng: ', `${String(rows.length).padStart(2, '0')} sinh viên`, signCell, '', '', '', '', signCell],
                ['', '', '', 'Trưởng phòng Tuyển sinh', '', '', '', '', 'Người lập'],
            ]
            const ws = XLSX.utils.aoa_to_sheet(aoa)
            const lastCol = HEADERS.length - 1
            const signRow = 7 + rows.length + 1
            ws['!merges'] = [
                { s: { r: 3, c: 0 }, e: { r: 3, c: 6 } },
                { s: { r: 4, c: 0 }, e: { r: 4, c: 6 } },
                //Trưởng phòng Tuyển sinh: gộp D:F | Người lập: gộp I:K (2 khối cân 2 bên bảng)
                { s: { r: signRow, c: 3 }, e: { r: signRow, c: 5 } },
                { s: { r: signRow + 1, c: 3 }, e: { r: signRow + 1, c: 5 } },
                { s: { r: signRow, c: 8 }, e: { r: signRow, c: 10 } },
                { s: { r: signRow + 1, c: 8 }, e: { r: signRow + 1, c: 10 } },
            ]
            ws['!cols'] = [6, 13, 24, 28, 16, 26, 8, 16, 14, 13, 26, 6, 13].map((wch) => ({ wch }))

            const thin = { style: 'thin', color: { rgb: 'CBD5E1' } }
            const borderAll = { top: thin, bottom: thin, left: thin, right: thin }
            const setStyle = (r: number, c: number, style: any) => {
                const addr = XLSX.utils.encode_cell({ r, c })
                if (!ws[addr]) ws[addr] = { t: 's', v: '' }
                ws[addr].s = style
            }
            setStyle(0, 1, { font: { sz: 11, bold: true }, alignment: { horizontal: 'left' } })
            setStyle(1, 2, { font: { sz: 11, bold: true }, alignment: { horizontal: 'left' } })
            for (let c = 0; c <= 6; c++) {
                setStyle(3, c, { font: { sz: 14, bold: true }, alignment: { horizontal: 'center', vertical: 'center' } })
                setStyle(4, c, { font: { sz: 10, italic: true }, alignment: { horizontal: 'center', vertical: 'center' } })
            }
            const HEADER_ROW = 6
            for (let c = 0; c <= lastCol; c++) {
                setStyle(HEADER_ROW, c, {
                    font: { sz: 11, bold: true },
                    fill: { patternType: 'solid', fgColor: { rgb: 'E2E8F0' } },
                    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                    border: borderAll,
                })
            }
            const TEXT_COLS = [1, 6, 8, 9] // MSSV, Điểm, CCCD, Điện thoại
            const CENTER_COLS = [0, 4, 6, 7, 11, 12]
            rows.forEach((_r, i) => {
                const r = HEADER_ROW + 1 + i
                for (let c = 0; c <= lastCol; c++) {
                    setStyle(r, c, {
                        font: { sz: 11 },
                        alignment: { horizontal: CENTER_COLS.includes(c) ? 'center' : 'left', vertical: 'center', wrapText: c === 3 || c === 5 },
                        border: borderAll,
                        ...(TEXT_COLS.includes(c) ? { numFmt: '@' } : {}),
                    })
                }
            })
            setStyle(signRow, 1, { font: { sz: 11, bold: true } })
            setStyle(signRow, 2, { font: { sz: 11, bold: true } })
            //style CẢ dải merge (D:F và I:K) — ô lẻ trong dải không có style thì Excel canh giữa hụt
            for (const c of [3, 4, 5, 8, 9, 10]) {
                setStyle(signRow, c, { font: { sz: 11, italic: true }, alignment: { horizontal: 'center', vertical: 'center' } })
                setStyle(signRow + 1, c, { font: { sz: 11, bold: true }, alignment: { horizontal: 'center', vertical: 'center' } })
            }
            ws['!rows'] = [{ hpt: 18 }, { hpt: 18 }, { hpt: 6 }, { hpt: 24 }, { hpt: 16 }, { hpt: 6 }, { hpt: 34 }]

            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'Đăng ký thi')
            const stamp = `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}-${p(now.getHours())}${p(now.getMinutes())}`
            XLSX.writeFile(wb, `du-lieu-xep-lop-tieng-anh-PDT-${stamp}.xlsx`)
        },
        async getData() {
            this.loading = true
            this.tableData = []
            this.initCustomers = []
            //BỎ chặn englishtestregistrationtype=umt_test: danh sách phải thấy đủ mọi hình thức xếp lớp
            //(IELTS/điểm THPT/không kiểm tra) thì mới đối chiếu được với Khởi tạo đồng bộ SIS. Lọc nhóm bằng dropdown.
            await axios.get(`applicant?status=completed,confirmed`)
                .then(({ data }) => {
                    this.tableData = this.sortWithAny(data.map(item => {
                        return {
                            ...item,
                            fullName: item.admissionRegistration?.fullName,
                            fullName1: this.removeAccents(item.admissionRegistration?.fullName),
                            createdAt: new Date(item.createdAt).toLocaleDateString("en-GB"),
                            testdate: item.englishClassPlacementTestRegistration?.testdate || '',
                            registrationType: item.englishClassPlacementTestRegistration?.registrationType || '',
                            englishMethod: this.englishMethodShort(item.englishClassPlacementTestRegistration?.registrationType),
                            statusLabel: this.statusLabel(item.status),
                            paid: this.isPaid(item),
                            received: this.isReceived(item),
                            paymentStatus: this.paymentStatusLabel(item),
                            programtype: this.programtypeLabel(item.programtype)
                        }
                    }), { sort: "testdate" })
                    this.initCustomers.splice(0, this.tableData.length, ...this.tableData);
                    this.applyFilter() //giữ bộ lọc đang chọn sau khi tải lại
                    this.loading = false
                })
                .catch(({ response }) => {
                    console.log('err', response)
                });
        },
        currentChange(currentPage, limit = 0) {
            if (limit == 0) {
                limit = this.pagination.limit
            } else {
                this.pagination.limit = limit
            }
            //this.getData()
        },
    },
    async created() {
        this.getData()
    },
    mounted() {
        //MenuComponent.bootstrap() chỉ chạy 1 lần lúc App mount -> vào trang phải reinit, không thì
        //bấm kebab không mở menu (mất luôn đường xuất Excel)
        MenuComponent.reinitialization()
    },
    setup() {
        const pagination = ref<IPagination>({
            currentPage: 1,
            total: 0,
            limit: 100,
        });
        const tableHeader = ref([
            // {
            //     name: "Ngày đăng ký",
            //     key: "testdate",
            //     sortable: true,
            //     class: ""
            // },
            {
                name: "MSSV",
                key: "studentCode",
                sortable: true,
                class: ""
            },
            {
                name: "Họ Tên",
                key: "fullName",
                sortable: false,
                class: ""
            },
            {
                name: "Ngành nhập học",
                key: "major",
                sortable: false,
                class: ""
            },
            {
                name: "Chương trình học",
                key: "programtype",
                sortable: false,
                class: ""
            },
            //3 cột thêm 2026-08 để đối chiếu với Khởi tạo đồng bộ SIS (sortable false: giá trị là nhãn đã map,
            //sort chuỗi ở đây không có ý nghĩa nghiệp vụ mà dễ vướng bug sort của Datatable)
            {
                name: "Hình thức xếp lớp",
                key: "englishMethod",
                sortable: false,
                class: ""
            },
            {
                name: "Trạng thái",
                key: "statusLabel",
                sortable: false,
                class: ""
            },
            {
                name: "Tình trạng hồ sơ",
                key: "paymentStatus",
                sortable: false,
                class: ""
            },
            {
                name: "Đợt",
                key: "phase",
                sortable: true,
                class: "text-center"
            },
            {
                name: "Ngày tạo",
                key: "createdAt",
                sortable: false,
                class: "text-end pe-0"
            },
        ]);
        const tableData = ref<Array<ICustomer>>([]);
        const initCustomers = ref<Array<ICustomer>>([]);
        const search = ref<string>("");
        //Tìm kiếm gộp vào applyFilter (methods) — searchItems cũ ghi đè tableData nên xoá mất bộ lọc dropdown
        const searchingFunc = (obj, value): boolean => {
            obj = JSON.parse(JSON.stringify(obj))
            for (let key in obj) {
                if (obj[key] && (typeof obj[key] === "string")) {
                    if (obj[key].toUpperCase().indexOf(value.toUpperCase()) != -1) {
                        return true;
                    }
                }
            }
            return false;
        };
        return {
            tableData,
            initCustomers,
            tableHeader,
            search,
            pagination,
            searchingFunc,
            Permission,
        };
    },
});
